import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../../../lib/supabase";
import { getTnStatus } from "../../../lib/tiendaNube";

/**
 * Estado REAL de conexión de las fuentes. Es el único dato verdadero del mapa
 * en el Bloque A: la píldora de "N de 3 fuentes conectadas" no es de ejemplo.
 *
 * Instagram: tabla `instagram_data` filtrada por `id_company` (mismo criterio
 * que `config_sections/InstagramIntegration`). Tiendanube: `getTnStatus`, que
 * invoca la edge function `tn-products` con `statusOnly`. Meta Ads: no existe
 * integración, siempre false.
 *
 * LOS BOOLEANOS SIGUEN CAYENDO A `false` ANTE UN ERROR — un fallo nunca puede
 * leerse como "conectado" — pero ahora ADEMÁS se reporta el fallo aparte
 * (`falloInstagram` / `falloTiendanube`). El mapa sigue contando fuentes con
 * los booleanos (una fuente que no se pudo consultar no se cuenta como
 * conectada); la pantalla de datos usa los `fallo*` para no decirle al usuario
 * "sin conectar" cuando lo cierto es "no pudimos preguntar"
 * (ADR-2026-08-17-estudio-marketing-datos-conexiones-y-fallo).
 */
export interface FuentesConectadas {
  instagram: boolean;
  tiendanube: boolean;
  meta_ads: false;
  cargando: boolean;
  /** La consulta a `instagram_data` no respondió. */
  falloInstagram: boolean;
  /** La edge function `tn-products` no respondió. */
  falloTiendanube: boolean;
  /**
   * Vuelve a consultar las dos fuentes. Identidad estable: no cambia entre
   * renders, así que se puede pasar a un `onClick` sin re-renderizar de más.
   */
  reintentar: () => void;
}

export function useFuentesConectadas(
  idCompany: string | null,
  brandId: string | null,
): FuentesConectadas {
  // Arranca en `cargando: true` con las tres en `false` — nunca al revés. Si
  // arrancara en `false`/`false` y después saltara a `true` cuando llega el
  // dato, el mapa parpadearía "no conectado" → "conectado" en cada carga; con
  // `cargando: true` de entrada, MapaPage puede directamente no dibujar la
  // píldora hasta tener la respuesta real. El efecto de abajo vuelve a poner
  // `cargando: true` cada vez que se dispara (no solo en este montaje
  // inicial), para que un cambio de marca a mitad de sesión tenga el mismo
  // cuidado — ver comentario ahí.
  const [estado, setEstado] = useState<Omit<FuentesConectadas, "reintentar">>({
    instagram: false,
    tiendanube: false,
    meta_ads: false,
    cargando: true,
    falloInstagram: false,
    falloTiendanube: false,
  });
  /** Cada "Reintentar" bumpea este contador y vuelve a correr el efecto. */
  const [intento, setIntento] = useState(0);
  const reintentar = useCallback(() => setIntento((n) => n + 1), []);

  useEffect(() => {
    let cancelado = false;
    (async () => {
      // Vuelve a "cargando: true" al arrancar la consulta de ESTA corrida
      // del efecto (no solo en el montaje inicial). Sin esto, al cambiar de
      // marca — mismo `idCompany`, `brandId` nuevo — `fuentes` se queda con
      // los valores de la marca anterior durante toda la ventana de red:
      // Tiendanube se filtra por marca, así que la píldora y el nodo
      // mostrarían la conexión VIEJA como si fuera la de la marca activa,
      // que es justo la mentira que este hook existe para eliminar.
      // Guardado detrás de `cancelado` por consistencia con el resto del
      // efecto, aunque en este punto (primera línea, antes de cualquier
      // `await`) todavía no pudo cancelarse.
      if (!cancelado) {
        setEstado((prev) => ({ ...prev, cargando: true }));
      }
      let instagram = false;
      let tiendanube = false;
      let falloInstagram = false;
      let falloTiendanube = false;
      try {
        if (idCompany) {
          // `maybeSingle` NO tira ante "cero filas" (devuelve `data: null` y
          // `error: null`), así que un `error` acá es una falla de verdad —
          // red, permisos o RLS — y no "esta empresa no tiene Instagram".
          const { data, error } = await supabase
            .from("instagram_data")
            .select("instagram_username")
            .eq("id_company", idCompany)
            .maybeSingle();
          if (error) falloInstagram = true;
          else instagram = Boolean(data?.instagram_username);
        }
      } catch {
        // Sin red, sin permisos o la tabla no responde: se asume NO
        // conectado. Un error acá nunca puede leerse como "conectado" — eso
        // sería mostrarle al usuario una fuente activa que no lo está. Pero
        // TAMPOCO es "sin conectar": queda marcado como fallo aparte.
        instagram = false;
        falloInstagram = true;
      }
      try {
        const tn = await getTnStatus(brandId);
        tiendanube = tn.connected;
        // `getTnStatus` no tira ante un error de la edge function: devuelve
        // `connected: false` con `error: true`. Sin mirar ese campo, una
        // función caída era indistinguible de una tienda no conectada.
        falloTiendanube = tn.error;
      } catch {
        // Mismo criterio: cubre además un rechazo de la promesa misma (p.ej.
        // la invocación no llega a resolver).
        tiendanube = false;
        falloTiendanube = true;
      }
      // Si el efecto se canceló (se desmontó el componente, o cambió
      // `idCompany`/`brandId` y ya hay una corrida más nueva en vuelo) esta
      // respuesta es de una consulta vieja: no pisa el estado ni dispara
      // `setState` sobre un componente que ya no está montado.
      if (!cancelado) {
        setEstado({
          instagram,
          tiendanube,
          meta_ads: false,
          cargando: false,
          falloInstagram,
          falloTiendanube,
        });
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [idCompany, brandId, intento]);

  // Objeto estable mientras no cambie nada: `reintentar` no se recrea y
  // `estado` sólo cambia cuando cambió de verdad, así que los consumidores que
  // lo pasan a un `useMemo`/`useEffect` no se disparan de más.
  return useMemo(() => ({ ...estado, reintentar }), [estado, reintentar]);
}
